import React, { useCallback, useEffect, useState } from 'react';
import { Btn, Loading, Msg, SubTabs, useAdminGet } from './kit';
import { type AdminTargetIn } from './adminNav';
import { type ConfigData, S, normConfigData } from './onboarding/shared';
import Settings from './onboarding/Settings';
import Keywords from './onboarding/Keywords';
import Picks from './onboarding/Picks';
import Funnel from './onboarding/Funnel';
import People from './onboarding/People';

/**
 * /admin → Onboarding (spec 13 Oct 2026): the first-run industry + keyword
 * steps, the precomputed Cosmos picks an Explorer lands on, and the
 * onboarding → trial / paid funnel, with every data point tracked.
 *
 *   Settings              flow switch, Go live / Roll back, industries shown
 *                         and their order, all modal + landing copy (preview)
 *   Industries & keywords the up-to-6 chips per industry (corpus or admin)
 *   Cosmos picks          the 84 + 14 + 1 precomputed lists: status, refresh,
 *                         pin / exclude, the random pool
 *   Funnel                shown → industry → keyword → explore / skip → landed
 *                         → locks → block clicks → trial → paid, with timings
 *                         and breakdowns
 *   People                one row per person, and their journey
 *
 * Backed by Backend/onboarding_admin.py and onboarding_picks.py (panel section
 * "onboarding"). One GET (config) feeds Settings and Industries & keywords;
 * their saves answer with the same shape (or the panel re-reads it). Tabs stay
 * mounted while another is open, so a half-done edit survives a look elsewhere.
 * Deep link: /admin?group=onboarding&tab=settings|keywords|picks|funnel|people.
 */

const TABS = ['Settings', 'Industries & keywords', 'Cosmos picks', 'Funnel', 'People'];
const TAB_KEYS: Record<string, string> = {
  settings: 'Settings', keywords: 'Industries & keywords', picks: 'Cosmos picks', funnel: 'Funnel', people: 'People',
};
const tabKey = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
const tabFromLink = (raw: string) =>
  TAB_KEYS[raw] || TABS.find(t => t === raw || tabKey(t) === tabKey(raw));

const OnboardingPanel: React.FC<{ target?: AdminTargetIn }> = ({ target }) => {
  const [tab, setTab] = useState(TABS[0]);
  useEffect(() => {
    if (!target) return;
    const t = tabFromLink(String(target.tab || '').trim());
    if (t) setTab(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.nonce]);
  // Funnel and People are heavier reads: mount them the first time they open.
  const [seen, setSeen] = useState<Record<string, boolean>>({ [TABS[0]]: true });
  useEffect(() => { setSeen(s => (s[tab] ? s : { ...s, [tab]: true })); }, [tab]);

  const res = useAdminGet<any>(S, 'config');
  const [data, setData] = useState<ConfigData | null>(null);
  useEffect(() => { if (res.data) setData(normConfigData(res.data)); }, [res.data]);
  const reload = res.reload;
  // A save that answers with the whole config replaces it; anything else re-reads.
  const onSaved = useCallback((d: any) => {
    if (d && typeof d === 'object' && (d.config || d.industries) ) setData(normConfigData(d));
    else reload();
  }, [reload]);

  const [dirty, setDirty] = useState<Record<string, boolean>>({});
  const markDirty = useCallback((t: string, v: boolean) =>
    setDirty(d => (!!d[t] === v ? d : { ...d, [t]: v })), []);
  const settingsDirty = useCallback((v: boolean) => markDirty(TABS[0], v), [markDirty]);
  const keywordsDirty = useCallback((v: boolean) => markDirty(TABS[1], v), [markDirty]);
  const elsewhere = TABS.filter(t => t !== tab && dirty[t]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <SubTabs tabs={TABS} active={tab} onChange={setTab} />
        <div className="flex items-center gap-3">
          {data && (
            <span className={`text-xs font-bold rounded-full px-2.5 py-0.5 ${data.config.flow === 'v2'
              ? 'text-green-800 bg-green-50 border border-green-200' : 'text-grayscale-1200 bg-grayscale-200'}`}>
              {data.config.flow === 'v2' ? 'Live (v2)' : 'Not live (v1)'}
            </span>
          )}
          <Btn small onClick={reload} disabled={res.loading}
            title="Re-read the saved settings (unsaved edits stay)">
            {res.loading && data ? 'Refreshing…' : 'Refresh'}
          </Btn>
        </div>
      </div>
      {elsewhere.length > 0 && (
        <div className="text-xs text-amber-700">
          Unsaved changes on {elsewhere.join(' and ')} — open {elsewhere.length === 1 ? 'that tab' : 'those tabs'} to
          save or discard them.
        </div>
      )}
      {res.error && (
        <Msg kind="error">
          Could not load the onboarding settings: {res.error}
          {' '}<button type="button" onClick={reload} className="underline">Retry</button>
        </Msg>
      )}
      {(tab === TABS[0] || tab === TABS[1] || tab === TABS[2]) && !data && res.loading && <Loading />}
      {data && (
        <>
          <div className={tab === TABS[0] ? '' : 'hidden'}>
            <Settings data={data} onSaved={onSaved} onDirty={settingsDirty} reload={reload} />
          </div>
          <div className={tab === TABS[1] ? '' : 'hidden'}>
            <Keywords data={data} onSaved={onSaved} onDirty={keywordsDirty} reload={reload} />
          </div>
          {seen[TABS[2]] && (
            <div className={tab === TABS[2] ? '' : 'hidden'}>
              <Picks data={data} />
            </div>
          )}
        </>
      )}
      {seen[TABS[3]] && <div className={tab === TABS[3] ? '' : 'hidden'}><Funnel /></div>}
      {seen[TABS[4]] && <div className={tab === TABS[4] ? '' : 'hidden'}><People industries={data?.all_names} /></div>}
    </div>
  );
};

export default OnboardingPanel;
